import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import translations from "@/data/translations";
import AnimatedSection from "./AnimatedSection";
import { ChevronDown } from "lucide-react";

const FAQSection = () => {
  const { lang } = useLang();
  const t = translations[lang].faq;

  return (
    <section id="faq" data-testid="faq-section" className="bg-slate-50 py-24 md:py-32">
      <div className="max-w-3xl mx-auto px-5 md:px-10">
        <AnimatedSection>
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-semibold text-electric mb-4 font-body">
            {t.overline}
          </p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-navy font-heading mb-12">
            {t.headline}
          </h2>
        </AnimatedSection>

        <div className="space-y-3">
          {t.items.map((item, i) => (
            <AnimatedSection key={i} delay={i * 0.04}>
              <FAQItem item={item} index={i} />
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

const FAQItem = ({ item, index }) => {
  const [open, setOpen] = useState(false);

  return (
    <div
      data-testid={`faq-item-${index}`}
      className={`rounded-xl border transition-all duration-300 ${
        open ? "bg-white border-electric/20 shadow-sm" : "bg-white border-slate-200 hover:border-slate-300"
      }`}
    >
      <button
        data-testid={`faq-toggle-${index}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
      >
        <span className="font-semibold text-navy text-sm md:text-base font-heading">{item.q}</span>
        <ChevronDown
          size={18}
          className={`shrink-0 text-slate-400 transition-transform duration-300 ${open ? "rotate-180 text-electric" : ""}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="px-6 pb-5 text-slate-500 text-sm leading-relaxed font-body">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FAQSection;
